/**
 * Forces the browser to use a specific filename for the PDF when the user
 * clicks the system print dialog's "Save as PDF" option.
 *
 * Chrome on Windows is known to sometimes use the *initial* `<title>` value
 * of the tab as the filename suggestion (instead of the live `document.title`).
 * To work around this we:
 *   1. Update `document.title` to our desired filename.
 *   2. Replace the actual `<title>` element node in the DOM (not just its text)
 *      so the browser picks up a fresh "intended" title.
 *   3. Wait one microtask to give the renderer time to commit the change.
 *   4. Call window.print().
 *
 * The optional `restore` boolean re-applies the previous title once the print
 * dialog closes (best-effort — Chrome blocks JS during the dialog).
 */
export async function printWithTitle(title, { restore = true } = {}) {
  if (!title) {
    window.print();
    return;
  }
  const prev = document.title;

  // (1) Standard property update.
  document.title = title;

  // (2) Replace the <title> element itself. Some browsers cache the original
  //     <title> text node and won't re-read it from the property update alone.
  try {
    const head = document.head;
    const oldEls = head.querySelectorAll("title");
    oldEls.forEach((el) => el.remove());
    const fresh = document.createElement("title");
    fresh.textContent = title;
    head.appendChild(fresh);
  } catch { /* noop */ }

  // (3) Yield to the renderer so the title commit is observed.
  await new Promise((r) => requestAnimationFrame(() => r()));
  await new Promise((r) => setTimeout(r, 0));

  // (4) Open the print dialog.
  window.print();

  if (restore) {
    // (5) Best-effort restore after the dialog closes.
    setTimeout(() => { document.title = prev; }, 1500);
  }
}
